import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const ART = 512;

function toSquareDataUrl(draw) {
  const c = document.createElement("canvas");
  c.width = ART;
  c.height = ART;
  draw(c.getContext("2d"));
  return c.toDataURL("image/jpeg", 0.9);
}

function initialsArt(title) {
  return toSquareDataUrl((ctx) => {
    ctx.fillStyle = "#1A1A1A";
    ctx.fillRect(0, 0, ART, ART);
    ctx.fillStyle = "#FF3B30";
    ctx.fillRect(0, ART - 24, ART, 24);
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.font = "900 300px Outfit, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText((title || "?").trim().charAt(0).toUpperCase(), ART / 2, ART / 2);
  });
}

async function coverArt(song) {
  const { data } = await api.get(`/songs/${song.id}/cover`, {
    responseType: "blob",
    params: { v: song.cover_updated_at },
  });
  const bmp = await createImageBitmap(data);
  return toSquareDataUrl((ctx) => {
    const r = Math.max(ART / bmp.width, ART / bmp.height);
    const w = bmp.width * r;
    const h = bmp.height * r;
    ctx.drawImage(bmp, (ART - w) / 2, (ART - h) / 2, w, h);
  });
}

// Publishes now-playing info to the OS (Windows media overlay, lock screen, wallpapers).
export default function useMediaSession(audioRef, { song, title, artist, playing, duration, skip }) {
  const [art, setArt] = useState("");
  const skipRef = useRef(skip);
  skipRef.current = skip;
  const ms = typeof navigator !== "undefined" && "mediaSession" in navigator ? navigator.mediaSession : null;

  useEffect(() => {
    if (!ms) return;
    let alive = true;
    const fallback = initialsArt(title);
    setArt(fallback);
    if (song?.id && song?.has_cover) {
      coverArt(song)
        .then((u) => alive && setArt(u))
        .catch(() => {});
    }
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song?.id, song?.has_cover, song?.cover_updated_at, title]);

  useEffect(() => {
    if (!ms || !window.MediaMetadata) return;
    ms.metadata = new window.MediaMetadata({
      title: title || "Untitled",
      artist: artist || "Unknown artist",
      album: "Lyriq",
      artwork: art ? [{ src: art, sizes: `${ART}x${ART}`, type: "image/jpeg" }] : [],
    });
  }, [ms, title, artist, art]);

  useEffect(() => () => ms && (ms.metadata = null), [ms]);

  useEffect(() => {
    if (ms) ms.playbackState = playing ? "playing" : "paused";
  }, [ms, playing]);

  useEffect(() => {
    if (!ms) return;
    const a = () => audioRef.current;
    const handlers = {
      play: () => a()?.play().catch(() => {}),
      pause: () => a()?.pause(),
      seekbackward: (d) => skipRef.current(-(d.seekOffset || 10)),
      seekforward: (d) => skipRef.current(d.seekOffset || 10),
      seekto: (d) => {
        if (a() && d.seekTime != null) a().currentTime = d.seekTime;
      },
    };
    Object.entries(handlers).forEach(([k, fn]) => {
      try {
        ms.setActionHandler(k, fn);
      } catch {}
    });
    return () => {
      Object.keys(handlers).forEach((k) => {
        try {
          ms.setActionHandler(k, null);
        } catch {}
      });
    };
  }, [ms, audioRef]);

  useEffect(() => {
    const el = audioRef.current;
    if (!ms?.setPositionState || !el || !duration || !isFinite(duration)) return;
    const sync = () => {
      try {
        ms.setPositionState({
          duration,
          playbackRate: el.playbackRate || 1,
          position: Math.min(el.currentTime || 0, duration),
        });
      } catch {}
    };
    sync();
    el.addEventListener("seeked", sync);
    el.addEventListener("ratechange", sync);
    return () => {
      el.removeEventListener("seeked", sync);
      el.removeEventListener("ratechange", sync);
    };
  }, [ms, audioRef, duration, playing]);
}
